import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Home, Mail } from 'lucide-react';
import { Link } from 'react-router';
import { getAllowedDomains } from '@/lib/auth/domain-validation';

export function AuthErrorPage() {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');

  const getErrorMessage = (error: string | null) => {
    switch (error) {
      case 'Configuration':
        return '認証設定に問題があります。管理者にお問い合わせください。';
      case 'AccessDenied':
        return 'アクセスが拒否されました。許可されたドメインのアカウントでログインしてください。';
      case 'Verification':
        return '認証の検証に失敗しました。';
      case 'Signin':
        return 'ワークスペースへのアクセス権限がありません。組織のメンバーかご確認ください。';
      default:
        return '認証中にエラーが発生しました。もう一度お試しください。';
    }
  };

  const getErrorDetails = (error: string | null) => {
    const allowedDomains = getAllowedDomains();

    switch (error) {
      case 'AccessDenied':
      case 'Signin':
        return (
          <div className="text-sm text-gray-600 mt-4">
            <p className="font-medium mb-2">許可されたドメイン:</p>
            <ul className="list-disc list-inside space-y-1">
              {allowedDomains.map((domain) => (
                <li key={domain}>@{domain}</li>
              ))}
            </ul>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertCircle className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-red-600">認証エラー</CardTitle>
          <CardDescription>{getErrorMessage(error)}</CardDescription>
          {getErrorDetails(error)}
        </CardHeader>
        <CardContent className="space-y-4">
          <Link to="/auth/signin">
            <Button className="w-full" variant="outline">
              再度ログインを試す
            </Button>
          </Link>
          <Link to="/">
            <Button className="w-full" variant="ghost">
              <Home className="mr-2 h-4 w-4" />
              ホームに戻る
            </Button>
          </Link>
          {(error === 'AccessDenied' || error === 'Signin') && (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">アクセス権限が必要ですか？</p>
              <Button variant="outline" size="sm" asChild>
                <a href="mailto:admin@your-company.com">
                  <Mail className="mr-2 h-4 w-4" />
                  管理者に連絡
                </a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
